// S15 — the fleet health board (spec 15 §4, §5 `GET /v1/platform/health`;
// M15-4, D-611, D-612). Every active tenant, worst first, from the latest
// window the collector finished: the dot, 24-hour uptime, error rate, p95,
// outbox lag, parked events and the probe's answer time. A row opens the
// tenant's health (S9).
//
// What the board does not show it says plainly: a window older than 15
// minutes is stale and every dot reads grey; a deployment with no backups to
// watch says "Not watched here" instead of an age. The audit chain and the
// restore drill have no source yet (D-484, D-485 ruling 4), so their columns
// are absent rather than empty (D-033).

import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../components/breadcrumbs";
import { Card } from "../../../components/card";
import { DataTable, type DataTableColumn } from "../../../components/data-table";
import { StatStrip, StatTile } from "../../../components/stat-strip";
import { EmptyState, ErrorState, TableSkeleton } from "../../../components/states";
import { StatusChip } from "../../../components/status-chip";
import { Banner } from "../../../components/toast";
import { formatTimestamp } from "../../../lib/datetime";
import { requestIdOf, useSignInAgainOn401 } from "../components/console-frame";
import { HealthDot } from "../components/health-dot";
import { ageSeconds, formatBp, formatMs, formatSeconds } from "../lib/health";
import { getHealthBoard, type HealthBoardRow } from "../lib/platform-api";

/** The board re-reads itself once a minute: a window lands every five. */
export const BOARD_REFRESH_MS = 60_000;

export function HealthBoardScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const board = useQuery({
    queryKey: ["platform", "health", "board"],
    queryFn: ({ signal }) => getHealthBoard(signal),
    refetchInterval: BOARD_REFRESH_MS,
  });
  useSignInAgainOn401(board.error);
  const locale = i18n.language;
  const dash = t("admin.health.none");

  const columns: DataTableColumn<HealthBoardRow>[] = [
    {
      key: "tenant",
      header: t("admin.health.colTenant"),
      render: (row) => (
        <span className="admin-name-cell">
          <span className="admin-strong">{row.tenant.name}</span>
          <code>{row.tenant.subdomain}</code>
          {row.tenant.sandbox ? (
            <StatusChip variant="accent" dot={false}>
              {t("admin.tenants.sandbox")}
            </StatusChip>
          ) : null}
        </span>
      ),
    },
    {
      key: "health",
      header: t("admin.health.colHealth"),
      kind: "status",
      render: (row) => <HealthDot health={row.health} />,
    },
    {
      key: "uptime",
      header: t("admin.health.colUptime"),
      numeric: true,
      render: (row) => formatBp(row.uptime_24h_bp, locale) ?? dash,
    },
    {
      key: "errors",
      header: t("admin.health.colErrors"),
      numeric: true,
      render: (row) =>
        row.snapshot === null || row.snapshot.request_count === 0
          ? dash
          : (formatBp(row.snapshot.http_error_rate_bp, locale) ?? dash),
    },
    {
      key: "p95",
      header: t("admin.health.colP95"),
      numeric: true,
      render: (row) => formatMs(row.snapshot?.p95_latency_ms ?? null, locale) ?? dash,
    },
    {
      key: "lag",
      header: t("admin.health.colLag"),
      numeric: true,
      render: (row) => formatSeconds(row.snapshot?.queue_lag_seconds ?? null, locale) ?? dash,
    },
    {
      key: "parked",
      header: t("admin.health.colParked"),
      numeric: true,
      render: (row) =>
        (row.snapshot?.queue_depth ?? null) === null ? dash : String(row.snapshot?.queue_depth),
    },
    {
      key: "alerts",
      header: t("admin.health.colAlerts"),
      render: (row) =>
        row.open_alerts.length === 0
          ? dash
          : row.open_alerts.map((k) => t(`admin.health.alertKind.${k}`)).join(", "),
    },
  ];

  let body;
  if (board.isPending) {
    body = <TableSkeleton rows={6} cols={6} label={t("admin.health.loading")} />;
  } else if (board.isError) {
    body = (
      <ErrorState
        title={t("admin.load.failed")}
        retryLabel={t("admin.load.retry")}
        onRetry={() => {
          void board.refetch();
        }}
        requestId={requestIdOf(board.error)}
        requestIdLabel={t("admin.load.requestId")}
      />
    );
  } else if (board.data.window === null) {
    body = (
      <EmptyState title={t("admin.health.noWindowTitle")} body={t("admin.health.noWindowBody")} />
    );
  } else {
    const { window, backup, fleet_alerts: fleetAlerts, items } = board.data;
    const now = new Date();
    const backupAge = ageSeconds(backup?.last_backup_at ?? null, now);
    body = (
      <>
        {window.stale ? (
          <Banner kind="warn" data-testid="admin-health-stale">
            {t("admin.health.stale")}
          </Banner>
        ) : null}
        {fleetAlerts.length > 0 ? (
          <Banner kind="bad" data-testid="admin-health-fleet-alerts">
            {fleetAlerts.map((a) => t(`admin.health.alertKind.${a.kind}`)).join(" · ")}
          </Banner>
        ) : null}
        <StatStrip data-testid="admin-health-stats">
          <StatTile label={t("admin.health.statProbed")} value={String(window.tenants_probed)} />
          <StatTile
            label={t("admin.health.statDown")}
            value={String(window.tenants_down)}
            data-testid="admin-health-stat-down"
          />
          <StatTile
            label={t("admin.health.statAlerts")}
            value={String(
              fleetAlerts.length + items.reduce((n, row) => n + row.open_alerts.length, 0),
            )}
          />
          <StatTile
            label={t("admin.health.statBackup")}
            value={
              backup === null || backup.source === "off"
                ? t("admin.health.backupOff")
                : backupAge === null
                  ? t("admin.health.backupNone")
                  : (formatSeconds(backupAge, locale) ?? dash)
            }
            {...(backup?.stale === true ? { sub: t("admin.health.backupStale") } : {})}
            data-testid="admin-health-stat-backup"
          />
        </StatStrip>
        <Card
          title={t("admin.health.tableTitle")}
          count={items.length}
          data-testid="admin-health-table"
        >
          {items.length === 0 ? (
            <EmptyState title={t("admin.health.emptyTitle")} body={t("admin.health.emptyBody")} />
          ) : (
            <DataTable
              columns={columns}
              rows={items}
              rowKey={(row) => row.tenant.id}
              empty={null}
              caption={t("admin.health.tableTitle")}
              rowTestId="admin-health-row"
              rowProps={(row) => ({
                "data-subdomain": row.tenant.subdomain,
                "data-dot": row.health.dot,
              })}
              onRowClick={(row) => {
                void navigate({
                  to: "/console/tenants/$tenantId/health",
                  params: { tenantId: row.tenant.id },
                });
              }}
            />
          )}
        </Card>
      </>
    );
  }

  const windowMeta =
    board.data?.window === null || board.data === undefined
      ? undefined
      : t("admin.health.windowMeta", {
          time: formatTimestamp(board.data.window.start, locale),
        });

  return (
    <section className="admin-screen" data-testid="admin-health-board">
      <PageHeader
        title={t("admin.health.title")}
        lede={t("admin.health.lede")}
        {...(windowMeta !== undefined ? { meta: windowMeta } : {})}
      />
      {body}
    </section>
  );
}
